import type {
    DinoTutorContext,
    JsonValue
} from "./context/dinoContextTopic.js";

type DinoAssistantFutureAction =
    | "explain"
    | "hint"
    | "whyWrong"
    | "simpler"
    | "translate"
    | "quiz"
    | "microphone"
    | "camera";

type DinoAssistantActionScores =
    Partial<Record<DinoAssistantFutureAction, number>>;

interface DinoAssistantRecommendationRule {
    readonly id: string;
    readonly score: (
        context: Readonly<DinoTutorContext>
    ) => DinoAssistantActionScores;
}

interface DinoAssistantRecommendationOptions {
    readonly limit?: number;
    /** Additional product or AI rules can enrich the deterministic baseline. */
    readonly rules?: readonly DinoAssistantRecommendationRule[];
}

const actionPriority: readonly DinoAssistantFutureAction[] = [
    "whyWrong",
    "hint",
    "explain",
    "simpler",
    "translate",
    "quiz",
    "microphone",
    "camera"
];

function normalize(value: string | undefined): string {
    return value?.trim().toLocaleLowerCase("en-US") ?? "";
}

function metadataText(value: JsonValue | undefined): string {
    if (value === undefined || value === null) {
        return "";
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value.map(metadataText).join(" ");
    }

    return Object.entries(value)
        .flatMap(([key, child]) => [key, metadataText(child)])
        .join(" ");
}

const baseRule: DinoAssistantRecommendationRule = {
    id: "baseline",
    score: () => ({
        explain: 10,
        simpler: 6,
        translate: 5,
        quiz: 4
    })
};

const pageRule: DinoAssistantRecommendationRule = {
    id: "page-kind",
    score: context => {
        const pageKind = normalize(context.page?.kind);

        if (/practice|daily|exercise|game/u.test(pageKind)) {
            return { hint: 32, quiz: 22, whyWrong: 16 };
        }

        if (/grammar/u.test(pageKind)) {
            return { explain: 32, hint: 17, quiz: 20 };
        }

        if (/vocabulary/u.test(pageKind)) {
            return { translate: 31, quiz: 24, explain: 20 };
        }

        if (/journal|travel/u.test(pageKind)) {
            return { simpler: 27, translate: 24, explain: 21 };
        }

        return {};
    }
};

const contentRule: DinoAssistantRecommendationRule = {
    id: "content-type",
    score: context => {
        const contentType = normalize(context.content?.type);

        if (/grammar|rule/u.test(contentType)) {
            return { explain: 28, hint: 15, quiz: 17 };
        }

        if (/vocab|word|lex/u.test(contentType)) {
            return { translate: 28, quiz: 20, explain: 16 };
        }

        if (/article|news|travel|story|lesson/u.test(contentType)) {
            return { simpler: 22, translate: 18, explain: 16 };
        }

        return {};
    }
};

const exerciseRule: DinoAssistantRecommendationRule = {
    id: "exercise-state",
    score: context => {
        if (context.exercise?.phase === "active") {
            return { hint: 42, whyWrong: 18, explain: 13 };
        }

        if (context.exercise?.phase === "completed") {
            return { quiz: 30, explain: 18, simpler: 13 };
        }

        return {};
    }
};

const learnerSignalRule: DinoAssistantRecommendationRule = {
    id: "learner-signal",
    score: context => {
        const signals = normalize([
            context.userAction?.type,
            context.userAction?.target,
            metadataText(context.metadata)
        ].filter(Boolean).join(" "));

        if (/incorrect|wrong|error|failed|mistake|faux|erreur/u.test(signals)) {
            return { whyWrong: 80, hint: 24, explain: 20 };
        }

        if (/hint|stuck|help|blocked|indice|aide/u.test(signals)) {
            return { hint: 70, explain: 18, simpler: 12 };
        }

        if (/pronunciation|speak|oral|microphone|prononciation/u.test(signals)) {
            return { microphone: 65, explain: 12, translate: 8 };
        }

        if (/image|camera|photo|vision/u.test(signals)) {
            return { camera: 65, explain: 12, translate: 8 };
        }

        return {};
    }
};

const defaultRecommendationRules = [
    baseRule,
    pageRule,
    contentRule,
    exerciseRule,
    learnerSignalRule
] as const satisfies readonly DinoAssistantRecommendationRule[];

function selectDinoAssistantActions(
    context: Readonly<DinoTutorContext>,
    options: DinoAssistantRecommendationOptions = {}
): readonly DinoAssistantFutureAction[] {
    const requestedLimit = options.limit ?? 3;
    const limit = Math.max(0, Math.min(3, Math.trunc(requestedLimit)));
    const scores = new Map<DinoAssistantFutureAction, number>();
    const rules = [
        ...defaultRecommendationRules,
        ...(options.rules ?? [])
    ];

    for (const rule of rules) {
        const contribution = rule.score(context);

        for (const action of actionPriority) {
            const score = contribution[action];

            if (score !== undefined && Number.isFinite(score)) {
                scores.set(action, (scores.get(action) ?? 0) + score);
            }
        }
    }

    return [...scores.entries()]
        .sort((left, right) => {
            const scoreDifference = right[1] - left[1];

            return scoreDifference !== 0
                ? scoreDifference
                : actionPriority.indexOf(left[0]) - actionPriority.indexOf(right[0]);
        })
        .slice(0, limit)
        .map(([action]) => action);
}

export {
    defaultRecommendationRules,
    selectDinoAssistantActions
};

export type {
    DinoAssistantActionScores,
    DinoAssistantFutureAction,
    DinoAssistantRecommendationOptions,
    DinoAssistantRecommendationRule
};
